import { FC } from 'react';
import { Link } from 'react-router-dom';
import { User, Calendar } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import type { TherapistAppointment } from '../../../types/therapist-dashboard.types';

interface TodayScheduleProps {
  appointments: TherapistAppointment[];
  isLoading?: boolean;
}

interface AppointmentItemProps {
  appointment: TherapistAppointment;
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const defaultStatusStyle = {
  bg: 'bg-gray-100 dark:bg-slate-700',
  text: 'text-gray-700 dark:text-slate-300',
  label: 'PENDING',
};

const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
  confirmed: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    label: 'CONFIRMED',
  },
  completed: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
    label: 'COMPLETED',
  },
  cancelled: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-300',
    label: 'CANCELLED',
  },
  no_show: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-300',
    label: 'NO SHOW',
  },
  pending: defaultStatusStyle,
};

function getStatusConfig(status: string) {
  return statusStyles[status] || defaultStatusStyle;
}

function AppointmentItem({ appointment }: AppointmentItemProps) {
  const startTime = formatTime(appointment.startTime);
  const endTime = formatTime(appointment.endTime);
  const config = getStatusConfig(appointment.status);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {appointment.clientAvatar ? (
            <img
              src={appointment.clientAvatar}
              alt={appointment.clientName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center">
              <User className="w-6 h-6 text-gray-500 dark:text-slate-400" />
            </div>
          )}
          <div>
            <div className="flex items-center space-x-2 mb-1">
              <h4 className="font-semibold text-gray-900 dark:text-slate-100">{appointment.clientName}</h4>
              {appointment.isNewClient && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                  NEW
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              {appointment.serviceName} - {appointment.duration} min
            </p>
          </div>
        </div>

        <div className="text-right">
          <p className="text-lg font-bold text-blue-600">
            {startTime} - {endTime}
          </p>
          <p className="text-sm text-gray-600 dark:text-slate-400">{formatCurrency(appointment.earnings)} earned</p>
        </div>
      </div>

      {appointment.specialRequests && (
        <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800">
          <strong>Special Request:</strong> {appointment.specialRequests}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <span
          className={cn('px-2 py-1 text-xs font-semibold rounded-full', config.bg, config.text)}
        >
          {config.label}
        </span>
        <Link
          to={`/therapist/appointments/${appointment.id}`}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
        >
          View Details
        </Link>
      </div>
    </div>
  );
}

export const TodaySchedule: FC<TodayScheduleProps> = ({ appointments, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!appointments || appointments.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg p-12 text-center border-2 border-dashed border-gray-300 dark:border-slate-600">
        <Calendar className="w-16 h-16 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">No Appointments Today</h3>
        <p className="text-gray-600 dark:text-slate-400 mb-6">
          Enjoy your free time or update your availability for upcoming days
        </p>
        <Link
          to="/therapist/dashboard?section=availability"
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Manage Availability
        </Link>
      </div>
    );
  }

  // Sort by start time
  const sortedAppointments = [...appointments].sort((a, b) =>
    a.startTime.localeCompare(b.startTime)
  );

  return (
    <div className="space-y-4">
      {sortedAppointments.map((appointment) => (
        <AppointmentItem key={appointment.id} appointment={appointment} />
      ))}
    </div>
  );
};
